/**
 * Admin Internal Interviews Page
 *
 * Full CRUD table for managing team member interview records. The internal
 * interview period ended 2026-10-07; this admin console is now the only way
 * to view or edit this data (the public tracker route was removed — see
 * docs/working-docs/ENGINEERING_DECISIONS.md's 2026-10-07 entry). Data is
 * retained.
 * Route: /internal-interviews
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck,
  ChatCircleDots,
  PencilSimple as Pencil,
  Plus,
  ArrowsClockwise as RefreshCw,
  Trash,
  UsersThree,
} from "@phosphor-icons/react";
import {
  EmptyState,
  HeaderAction,
  HeaderIconButton,
  PageHeader,
  type PillTone,
  StatTile,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@charisol-spark/ui";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import { Spinner } from "@/components/ui/Spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  useAdminInterviews,
  useCreateTeamMember,
  useDeleteTeamMember,
  useUpdateTeamMember,
} from "@/hooks/useInterviews";
import type {
  CreateTeamMemberInput,
  TeamMember,
  UpdateTeamMemberInput,
} from "@/services/interviews";
import { cn } from "@/utils/cn";

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "createdAt", label: "Added" },
  { field: "interviewDate", label: "Interview date" },
  { field: "name", label: "Name", kind: "text" },
];

/** Most recently added first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  filterKeys: ["team", "status", "interviewer"],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "createdAt", dir: "desc" },
};

const SORT_GETTERS: Record<string, (member: TeamMember) => string | undefined> =
  {
    createdAt: (m) => m.createdAt,
    interviewDate: (m) => m.interviewDate,
    name: (m) => m.name.toLowerCase(),
  };

const STATUS_OPTIONS = ["", "Contacted", "Scheduled", "Interviewed"];
const REWARD_OPTIONS = ["0/3", "1/3", "2/3", "3/3"];

/** Labels shown in admin tooltips / form context. */
const REWARD_LABELS: Record<string, string> = {
  "0/3": "No rewards yet",
  "1/3": "Joined interview",
  "2/3": "First post made",
  "3/3": "Quarter Champion",
};

const STATUS_TONES: Record<string, PillTone> = {
  Interviewed: "green",
  Contacted: "amber",
  Scheduled: "blue",
};

// ── Form Dialog ────────────────────────────────────────────

interface MemberFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member?: TeamMember | null;
}

function MemberFormDialog({ open, onOpenChange, member }: MemberFormProps) {
  const isEdit = !!member;
  const createMutation = useCreateTeamMember();
  const updateMutation = useUpdateTeamMember();

  const [form, setForm] = useState<CreateTeamMemberInput>({
    team: "",
    name: "",
    role: "",
    email: "",
    whatsApp: "",
    status: "",
    rewardGiven: "0/3",
    notes: "",
  });

  // Sync form when member changes
  const resetForm = useCallback((m?: TeamMember | null) => {
    if (m) {
      setForm({
        team: m.team,
        name: m.name,
        role: m.role,
        email: m.email,
        whatsApp: m.whatsApp,
        status: m.status,
        rewardGiven: m.rewardGiven,
        notes: m.notes,
      });
    } else {
      setForm({
        team: "",
        name: "",
        role: "",
        email: "",
        whatsApp: "",
        status: "",
        rewardGiven: "0/3",
        notes: "",
      });
    }
  }, []);

  // Sync form whenever the dialog opens or the member being edited changes.
  // Using useEffect instead of handleOpenChange because Radix Dialog only fires
  // onOpenChange on internal events (Escape, overlay click), not when the parent
  // programmatically sets open={true}.
  useEffect(() => {
    if (open) resetForm(member);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member]);

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (isEdit && member) {
        const input: UpdateTeamMemberInput = {};
        for (const [key, value] of Object.entries(form)) {
          if (value !== (member as unknown as Record<string, unknown>)[key]) {
            (input as Record<string, unknown>)[key] = value;
          }
        }
        await updateMutation.mutateAsync({ id: member.id, input });
      } else {
        await createMutation.mutateAsync(form);
      }
      onOpenChange(false);
    } catch {
      // Mutation error is available via createMutation.error / updateMutation.error
      // and displayed below the form
    }
  }

  const fieldClass =
    "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Member" : "Add Member"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this team member's details."
              : "Add a new team member to the interviews tracker."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Name *
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={fieldClass}
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Team *
              </label>
              <input
                required
                value={form.team}
                onChange={(e) => setForm({ ...form, team: e.target.value })}
                className={fieldClass}
                placeholder="Engineering"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Role
              </label>
              <input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className={fieldClass}
                placeholder="Backend Developer"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={fieldClass}
                placeholder="john@charisol.io"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                WhatsApp
              </label>
              <input
                value={form.whatsApp}
                onChange={(e) => setForm({ ...form, whatsApp: e.target.value })}
                className={fieldClass}
                placeholder="+234..."
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={fieldClass}
                aria-label="Status"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s || "Not contacted"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Reward Given
              </label>
              <select
                value={form.rewardGiven}
                onChange={(e) =>
                  setForm({ ...form, rewardGiven: e.target.value })
                }
                className={fieldClass}
                aria-label="Reward given"
              >
                {REWARD_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r} — {REWARD_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Researcher's summary — admin only, never shown on public page */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Researcher's Summary
              <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
                (admin only)
              </span>
            </label>
            <textarea
              value={form.notes ?? ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              maxLength={5000}
              className={`${fieldClass} resize-y`}
              placeholder="Add researcher's summary, follow-up actions, or any relevant context…"
            />
          </div>

          {/* Mutation error display */}
          {(createMutation.error || updateMutation.error) && (
            <Alert variant="error">
              {(createMutation.error || updateMutation.error)?.message ||
                "Something went wrong. Please try again."}
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              pill
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" pill disabled={isPending}>
              {isPending ? <Spinner size="sm" className="mr-1.5" /> : null}
              {isEdit ? "Save Changes" : "Add Member"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Confirmation ────────────────────────────────────

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember | null;
}

function DeleteConfirmDialog({
  open,
  onOpenChange,
  member,
}: DeleteDialogProps) {
  const deleteMutation = useDeleteTeamMember();

  async function handleDelete() {
    if (!member) return;
    try {
      await deleteMutation.mutateAsync(member.id);
      onOpenChange(false);
    } catch {
      // Error shown via deleteMutation.error if needed
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Team Member</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove <strong>{member?.name}</strong> from
            the tracker? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            pill
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            pill
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <Spinner size="sm" className="mr-1.5" />
            ) : null}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────

export function AdminInterviewsPage() {
  const {
    data: members,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useAdminInterviews();

  const navigate = useNavigate();

  const list = useListUrlState(URL_CONFIG);
  const { filters } = list;

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteMember, setDeleteMember] = useState<TeamMember | null>(null);

  const teams = useMemo(() => {
    if (!members) return [];
    return [...new Set(members.map((m) => m.team))].sort();
  }, [members]);

  // The whole roster is loaded at once, so filtering and sorting here is
  // complete, not limited to a page.
  const filtered = useMemo(() => {
    if (!members) return [];
    const q = list.search.toLowerCase();
    const matching = members.filter((m) => {
      if (filters.team && m.team !== filters.team) return false;
      if (filters.status && m.status !== filters.status) return false;
      if (
        filters.interviewer &&
        !m.interviewer.toLowerCase().includes(filters.interviewer.toLowerCase())
      ) {
        return false;
      }
      return (
        !q ||
        m.name.toLowerCase().includes(q) ||
        m.role.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q)
      );
    });
    return sortRows(matching, list.sort, SORT_GETTERS);
  }, [members, list.search, list.sort, filters]);

  // Overview counts — the one thing the now-removed public tracker showed
  // that this page didn't: a quick read of where the whole roster stands,
  // not just the filtered table below.
  const counts = useMemo(() => {
    if (!members) return { total: 0, interviewed: 0, contacted: 0 };
    return {
      total: members.length,
      interviewed: members.filter((m) => m.status === "Interviewed").length,
      contacted: members.filter(
        (m) => m.status === "Contacted" || m.status === "Scheduled"
      ).length,
    };
  }, [members]);

  function openCreate() {
    setEditMember(null);
    setFormOpen(true);
  }

  function openEdit(m: TeamMember) {
    setEditMember(m);
    setFormOpen(true);
  }

  function openDelete(m: TeamMember) {
    setDeleteMember(m);
    setDeleteOpen(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        variant="classic"
        title="Internal Interviews"
        description="Manage team member interview tracking for Spark adoption."
        action={
          <>
            <HeaderAction
              icon={<Plus className="h-4 w-4" />}
              onClick={openCreate}
            >
              Add Member
            </HeaderAction>
            <HeaderIconButton label="Refresh" onClick={() => refetch()}>
              <RefreshCw
                className={cn("h-4.5 w-4.5", isFetching && "animate-spin")}
              />
            </HeaderIconButton>
          </>
        }
      />

      <AdminListToolbar
        label="Team members"
        search={{
          value: list.search,
          onChange: list.setSearch,
          placeholder: "Search by name, role, or email",
        }}
        filters={[
          {
            id: "team",
            label: "Team",
            kind: "select",
            options: [
              { value: "", label: "All teams" },
              ...teams.map((t) => ({ value: t, label: t })),
            ],
            value: filters.team!,
            onChange: (v) => list.setFilter("team", v),
          },
          {
            id: "status",
            label: "Status",
            kind: "select",
            options: [
              { value: "", label: "All statuses" },
              ...STATUS_OPTIONS.filter(Boolean).map((v) => ({
                value: v,
                label: v,
              })),
            ],
            value: filters.status!,
            onChange: (v) => list.setFilter("status", v),
          },
          {
            id: "interviewer",
            label: "Interviewer",
            kind: "text",
            placeholder: "Any interviewer",
            value: filters.interviewer!,
            onChange: (v) => list.setFilter("interviewer", v.trim()),
          },
        ]}
        sort={{
          options: SORT_OPTIONS,
          value: list.sort,
          onChange: list.setSort,
          scope: "server",
        }}
        hasActive={list.hasActive}
        onClear={list.clear}
      />

      {/* Overview counts */}
      {!isLoading && members && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatTile
            label="Members"
            value={counts.total}
            icon={UsersThree}
            tone="violet"
          />
          <StatTile
            label="Interviewed"
            value={counts.interviewed}
            icon={CalendarCheck}
            tone="emerald"
          />
          <StatTile
            label="Contacted / scheduled"
            value={counts.contacted}
            icon={ChatCircleDots}
            tone="amber"
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <Alert variant="error">Failed to load team members.</Alert>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border/50 bg-card">
          <EmptyState
            icon={UsersThree}
            title="No team members found."
            description="Adjust the search or filters, or add a member."
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>Name</TH>
                <TH className="hidden sm:table-cell">Team</TH>
                <TH className="hidden md:table-cell">Role</TH>
                <TH className="hidden md:table-cell">Email</TH>
                <TH>Status</TH>
                <TH className="hidden lg:table-cell">Reward</TH>
                <TH className="hidden xl:table-cell">Summary</TH>
                <TH className="w-24 text-center">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((m) => (
                <TR
                  key={m.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/internal-interviews/${m.id}`)}
                >
                  <TD className="whitespace-nowrap font-medium">{m.name}</TD>
                  <TD className="hidden text-muted-foreground sm:table-cell">
                    {m.team}
                  </TD>
                  <TD className="hidden text-muted-foreground md:table-cell">
                    {m.role || "—"}
                  </TD>
                  <TD className="hidden text-muted-foreground md:table-cell">
                    {m.email || "—"}
                  </TD>
                  <TD>
                    {m.status ? (
                      <StatusPill tone={STATUS_TONES[m.status] ?? "neutral"}>
                        {m.status}
                      </StatusPill>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TD>
                  <TD className="hidden text-muted-foreground lg:table-cell">
                    {m.rewardGiven === "0/3" ? "—" : m.rewardGiven}
                  </TD>
                  <TD className="hidden max-w-xs xl:table-cell">
                    {m.notes ? (
                      <p className="line-clamp-2 whitespace-pre-wrap text-sm text-muted-foreground">
                        {m.notes}
                      </p>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TD>
                  <TD>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(m);
                        }}
                        className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        aria-label={`Edit ${m.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDelete(m);
                        }}
                        className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        aria-label={`Delete ${m.name}`}
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {/* Dialogs */}
      <MemberFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        member={editMember}
      />
      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        member={deleteMember}
      />
    </div>
  );
}
